/**
 * Fleet tab (MyGeotab)
 * ====================
 * Third tab on the Patrol page, visible only to users granted fleet_access
 * (or admins). Read-only: live vehicle locations, fuel levels straight off
 * the vehicle, and a per-car history of who had it when.
 *
 * Purely additive — the manual fuel level, patrol logins and everything on
 * the Patrol/FSD tabs are untouched. Where both exist (fuel), both are shown
 * side by side rather than one overwriting the other.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Car,
  Fuel,
  MapPin,
  RefreshCw,
  Gauge,
  Clock,
  History,
  Users,
  AlertTriangle,
  Navigation,
  Satellite,
  Filter,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

// Vehicle pin: green when moving, slate when parked.
const carIcon = (moving) =>
  L.divIcon({
    className: "fleet-marker",
    html: `<div style="
        width:20px;height:20px;border-radius:50%;
        background:${moving ? "#22c55e" : "#64748b"};
        border:3px solid white;
        box-shadow:0 2px 6px rgba(0,0,0,.45);
      "></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -10],
  });

// Fit the view to wherever the cars actually are, instead of a fixed zoom.
// Re-fits only when the set of plotted cars changes, so a 60s refresh doesn't
// yank the map back while someone is panning around.
function FleetBounds({ points }) {
  const map = useMap();
  const lastKey = useRef("");
  useEffect(() => {
    if (!points.length) return;
    const key = points.map((p) => p.car_id).sort().join(",");
    if (key === lastKey.current) return;
    lastKey.current = key;
    if (points.length === 1) {
      map.setView([points[0].location.latitude, points[0].location.longitude], 16);
    } else {
      const bounds = L.latLngBounds(
        points.map((p) => [p.location.latitude, p.location.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
  }, [points, map]);
  return null;
}

const HISTORY_WINDOWS = [
  { value: "6", label: "Last 6 hours" },
  { value: "24", label: "Last 24 hours" },
  { value: "72", label: "Last 3 days" },
  { value: "168", label: "Last 7 days" },
];

const kphToMph = (kph) =>
  typeof kph === "number" ? Math.round(kph * 0.621371) : null;

const kmToMiles = (km) =>
  typeof km === "number" ? Math.round(km * 0.621371 * 10) / 10 : null;

const ago = (iso) => {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
};

const stamp = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "MMM d, h:mm a");
  } catch {
    return "—";
  }
};

// History rows. Explicit light/dark pairs rather than the bare slate classes
// used elsewhere in this file: App.css rewrites `.light .bg-slate-950`, but the
// opacity variant `bg-slate-950/50` is a different class name and slipped past
// it, leaving force-darkened text sitting on a still-dark row in light mode.
const ROW =
  "p-2 rounded border border-slate-200 dark:border-slate-800 " +
  "bg-slate-50 dark:bg-slate-950/50 text-sm";

// Fuel gets the same color language as the manual badge on the FSD tab.
const fuelColor = (percent) => {
  if (typeof percent !== "number") return "border-slate-400 dark:border-slate-600 text-slate-400";
  if (percent <= 12.5) return "border-red-500 text-red-600 dark:text-red-400";
  if (percent <= 37.5) return "border-amber-500 text-amber-600 dark:text-amber-400";
  return "border-green-500 text-green-600 dark:text-green-400";
};

export default function PatrolFleet() {
  const [cars, setCars] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [error, setError] = useState(null);

  // Which cars are hidden from the map, remembered between visits.
  const [hiddenCarIds, setHiddenCarIds] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("fleetMapHidden") || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });
  const [filterOpen, setFilterOpen] = useState(false);

  const setHidden = (ids) => {
    setHiddenCarIds(ids);
    try {
      localStorage.setItem("fleetMapHidden", JSON.stringify(ids));
    } catch {
      /* private mode / quota — filtering still works for this session */
    }
  };

  const toggleCar = (carId) =>
    setHidden(
      hiddenCarIds.includes(carId)
        ? hiddenCarIds.filter((id) => id !== carId)
        : [...hiddenCarIds, carId]
    );

  // Per-car history
  const [historyCar, setHistoryCar] = useState(null);
  const [historyHours, setHistoryHours] = useState("24");
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Kept outside the dialog so the chosen tab survives switching cars —
  // if you were reading trips, you're probably still reading trips.
  const [historyTab, setHistoryTab] = useState("drivers");

  const loadFleet = useCallback(async (isRefresh) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/geotab/fleet`, { withCredentials: true });
      setCars(res.data?.cars || []);
      setUnlinked(res.data?.unlinked || []);
      setConfigured(true);
      setError(null);
    } catch (e) {
      const status = e.response?.status;
      if (status === 503) {
        setConfigured(false);
        setError(e.response?.data?.detail || "MyGeotab is not configured");
      } else {
        setError(e.response?.data?.detail || "Could not load fleet data");
        if (isRefresh) toast.error(e.response?.data?.detail || "Could not load fleet data");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadFleet(false);
    // Vehicles report every minute or so; a 60s poll keeps the tab live
    // without hammering the Geotab API.
    const timer = setInterval(() => loadFleet(true), 60000);
    return () => clearInterval(timer);
  }, [loadFleet]);

  const openHistory = async (car, hours = historyHours) => {
    setHistoryCar(car);
    setHistory(null);
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/geotab/cars/${car.car_id}/history?hours=${hours}`,
        { withCredentials: true }
      );
      setHistory(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load vehicle history");
      setHistoryCar(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const changeWindow = (hours) => {
    setHistoryHours(hours);
    if (historyCar) openHistory(historyCar, hours);
  };

  const plotted = cars.filter(
    (c) => typeof c.location?.latitude === "number" && typeof c.location?.longitude === "number"
  );
  // Map filter applies to the pins only — the cards below always list every
  // tracked car. Hiding is stored by car_id, so a car that appears later
  // starts visible rather than inheriting someone else's filter.
  const visible = plotted.filter((c) => !hiddenCarIds.includes(c.car_id));
  const allShown = visible.length === plotted.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!configured) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="py-12 text-center text-slate-400">
          <Satellite className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="font-semibold">MyGeotab is not connected</p>
          <p className="text-sm mt-2 max-w-md mx-auto">
            Set GEOTAB_USERNAME, GEOTAB_PASSWORD and GEOTAB_DATABASE on the backend,
            then link each patrol car to its Geotab vehicle in Settings → Patrol → Cars.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-400">
          {cars.length} {cars.length === 1 ? "vehicle" : "vehicles"} tracked
          {plotted.length < cars.length && ` · ${cars.length - plotted.length} without a position`}
        </p>
        <div className="flex items-center gap-2">
          {plotted.length > 0 && (
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-1" />
                  {allShown ? "All cars" : `${visible.length}/${plotted.length}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 bg-slate-900 border-slate-800 p-0">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Show on map
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setHidden([])}
                      disabled={allShown}
                    >
                      All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setHidden(plotted.map((c) => c.car_id))}
                      disabled={visible.length === 0}
                    >
                      None
                    </Button>
                  </div>
                </div>
                <ScrollArea className="max-h-64">
                  <div className="p-1">
                    {plotted.map((car) => {
                      const shown = !hiddenCarIds.includes(car.car_id);
                      return (
                        <div
                          key={car.car_id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 cursor-pointer"
                          onClick={() => toggleCar(car.car_id)}
                        >
                          <Checkbox checked={shown} className="pointer-events-none" />
                          <span className="text-sm font-mono">{car.car_name}</span>
                          {car.assigned_unit && (
                            <span className="text-xs text-slate-500 ml-auto">
                              {car.assigned_unit}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}
          <Button variant="outline" size="sm" onClick={() => loadFleet(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded border border-red-800/50 bg-red-900/20 text-sm text-red-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {cars.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="py-12 text-center text-slate-500">
            <Car className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No patrol cars are linked to a Geotab vehicle yet</p>
            <p className="text-sm mt-1">Link them in Settings → Patrol → Cars → Edit</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* relative z-0 puts the whole map in its own stacking context —
              Leaflet's panes and controls sit at z-index 400-1000 and would
              otherwise paint straight over any dialog opened on this tab. */}
          {plotted.length > 0 && (
            <Card className="bg-slate-900 border-slate-800 overflow-hidden relative z-0">
              <div className="h-[320px]">
                <MapContainer
                  center={[plotted[0].location.latitude, plotted[0].location.longitude]}
                  zoom={15}
                  scrollWheelZoom={true}
                  style={{ height: "100%", width: "100%" }}
                >
                  <FleetBounds points={visible} />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {visible.map((car) => (
                    <Marker
                      key={car.car_id}
                      position={[car.location.latitude, car.location.longitude]}
                      icon={carIcon(car.location.is_driving)}
                    >
                      <Popup>
                        <div className="text-sm">
                          <div className="font-bold">{car.car_name}</div>
                          {car.assigned_unit && <div>Unit {car.assigned_unit}</div>}
                          <div>
                            {car.location.is_driving
                              ? `Moving · ${kphToMph(car.location.speed_kph) ?? "?"} mph`
                              : "Parked"}
                          </div>
                          {car.location.address && (
                            <div className="mt-1">{car.location.address}</div>
                          )}
                          {typeof car.fuel?.percent === "number" && (
                            <div>Fuel {car.fuel.percent}%</div>
                          )}
                          <div className="text-slate-500">{ago(car.location.reported_at) || "no report"}</div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cars.map((car) => {
              const loc = car.location || {};
              const hasFix = typeof loc.latitude === "number" && typeof loc.longitude === "number";
              return (
                <Card key={car.car_id} className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-lg font-mono">{car.car_name}</CardTitle>
                      {loc.is_driving ? (
                        <Badge className="bg-green-600 text-white">
                          <Navigation className="w-3 h-3 mr-1" />Moving
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-slate-600 text-slate-400">Parked</Badge>
                      )}
                    </div>
                    {car.geotab_device_name && car.geotab_device_name !== car.car_name && (
                      <p className="text-xs text-slate-500">Geotab: {car.geotab_device_name}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {car.assigned_unit && (
                      <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-200">
                        <Users className="w-4 h-4" />
                        <span className="font-semibold">{car.assigned_unit}</span>
                        <span className="text-xs text-slate-500">(logged in)</span>
                      </div>
                    )}

                    {/* Fuel — Geotab reading next to whatever was entered manually */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`text-xs ${fuelColor(car.fuel?.percent)}`}>
                        <Fuel className="w-3 h-3 mr-1" />
                        {typeof car.fuel?.percent === "number"
                          ? `${car.fuel.percent}%${car.fuel.as_eighths ? ` · ${car.fuel.as_eighths}` : ""}`
                          : "No fuel data"}
                      </Badge>
                      {car.manual_fuel_level && (
                        <span className="text-xs text-slate-500">
                          manual: {car.manual_fuel_level}
                        </span>
                      )}
                    </div>

                    {/* Position */}
                    {hasFix ? (
                      <div className="space-y-1 text-sm">
                        <a
                          href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-slate-300 hover:text-blue-400"
                        >
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span className="font-mono text-xs">
                            {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
                          </span>
                        </a>
                        {loc.address && (
                          <p className="text-xs text-slate-300 pl-6 leading-snug">{loc.address}</p>
                        )}
                        {loc.is_driving && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Gauge className="w-3 h-3" />
                            {kphToMph(loc.speed_kph) ?? "?"} mph
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {ago(loc.reported_at) || "no report time"}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No position reported</p>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => openHistory(car)}
                    >
                      <History className="w-3 h-3 mr-1" />Driver &amp; trip history
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {unlinked.length > 0 && (
            <p className="text-xs text-slate-500">
              Not linked to Geotab: {unlinked.join(", ")}
            </p>
          )}
        </>
      )}

      {/* History dialog */}
      <Dialog
        open={!!historyCar}
        onOpenChange={(open) => { if (!open) { setHistoryCar(null); setHistory(null); } }}
      >
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle>{historyCar?.car_name} — driver &amp; trip history</DialogTitle>
          </DialogHeader>

          <div className="mt-2">
            <Label className="text-xs text-slate-400">Window</Label>
            <Select value={historyHours} onValueChange={changeWindow}>
              <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HISTORY_WINDOWS.map((w) => (
                  <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            /* Driver changes and trips are separate tabs — they answer different
               questions ("who had it" vs "where did it go") and stacking them
               buried the trips behind a long driver list. */
            <Tabs value={historyTab} onValueChange={setHistoryTab} className="mt-2">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="drivers">
                  Drivers ({history?.driver_changes?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="trips">
                  Trips ({history?.trips?.length || 0})
                </TabsTrigger>
              </TabsList>

              {/* Geotab's driver record — patrol logins live on the FSD tab */}
              <TabsContent value="drivers">
                <ScrollArea className="h-[420px] pr-3">
                  {history?.driver_changes?.length ? (
                    <div className="space-y-2">
                      {history.driver_changes.map((d, i) => (
                        <div key={i} className={ROW}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                              {d.driver_name || (d.driver_id ? d.driver_id : "Unknown driver")}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {stamp(d.changed_at)}
                            </span>
                          </div>
                          {d.type && (
                            <div className="text-xs text-slate-600 dark:text-slate-400">{d.type}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600 dark:text-slate-400 py-2">
                      No driver records in this window — Geotab only identifies the driver
                      when the vehicle has driver-ID keys or the Geotab Drive app.
                    </p>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="trips">
                <ScrollArea className="h-[420px] pr-3">
                  {history?.trips?.length ? (
                    <div className="space-y-2">
                      {history.trips.map((t, i) => (
                        <div key={i} className={ROW}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-800 dark:text-slate-200">
                              {stamp(t.started_at)} → {stamp(t.ended_at)}
                            </span>
                            {t.driver_name && (
                              <span className="text-xs text-emerald-700 dark:text-emerald-300">
                                {t.driver_name}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400">
                            {kmToMiles(t.distance_km) != null && `${kmToMiles(t.distance_km)} mi`}
                            {typeof t.driving_minutes === "number" && ` · ${t.driving_minutes} min driving`}
                            {typeof t.idling_minutes === "number" && t.idling_minutes > 0 &&
                              ` · ${t.idling_minutes} min idling`}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600 dark:text-slate-400 py-2">
                      No trips in this window
                    </p>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
